/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { speak } from "../lib/i18n";
import { Sunrise, Moon, Volume2, VolumeX, Check, Circle } from "lucide-react";

export const briefEnabled = () => localStorage.getItem("bc_brief_enabled") !== "0";
export const setBriefEnabled = (on) => localStorage.setItem("bc_brief_enabled", on ? "1" : "0");
export const EVENING_HOUR = 18;

const today = () => new Date().toISOString().slice(0, 10);
const modeNow = () => (new Date().getHours() >= EVENING_HOUR ? "soir" : "matin");

/** Résumé vocal : le matin (programme du jour) et le soir (bilan + ce qui reste), lus à la 1re ouverture, réécoutables. */
export function MorningBrief() {
  const { patient, lang, online } = useApp();
  const mode = modeNow();
  const cacheKey = `bc_brief_cache_${mode}`;
  const [brief, setBrief] = useState(() => { try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; } });
  const [speaking, setSpeaking] = useState(false);
  const autoDone = useRef(false);

  useEffect(() => {
    if (!patient) return;
    api.get(`/dashboard/${mode === "soir" ? "evening-brief" : "morning-brief"}?patient_id=${patient.id}`)
      .then(({ data }) => { setBrief(data); localStorage.setItem(cacheKey, JSON.stringify(data)); })
      .catch(() => null);
  }, [patient, online, mode]); // eslint-disable-line

  const text = brief ? (lang === "ln" ? brief.texte_ln : brief.texte_fr) : "";

  const audioRef = useRef(null);
  const [voice, setVoice] = useState(null); // "naturelle" | "navigateur"

  const play = async (txt) => {
    if (!txt) return;
    setSpeaking(true);
    try {
      if (!navigator.onLine) throw new Error("offline");
      const { data } = await api.post("/tts/brief", { patient_id: patient.id, mode, langue: lang }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await a.play();
      setVoice("naturelle");
      return;
    } catch {
      setVoice("navigateur");
    }
    if (!("speechSynthesis" in window)) { setSpeaking(false); return; }
    speak(txt, lang);
    const poll = setInterval(() => { if (!window.speechSynthesis.speaking) { setSpeaking(false); clearInterval(poll); } }, 400);
  };
  const stopSpeech = () => { window.speechSynthesis?.cancel(); if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setSpeaking(false); };

  useEffect(() => {
    if (!brief || autoDone.current || !briefEnabled()) return;
    const hour = new Date().getHours();
    const key = mode === "soir" ? "bc_brief_spoken_soir" : "bc_brief_spoken";
    const ok = mode === "soir" ? hour >= EVENING_HOUR : hour < 12;
    if (ok && localStorage.getItem(key) !== today()) {
      autoDone.current = true;
      localStorage.setItem(key, today());
      play(lang === "ln" ? brief.texte_ln : brief.texte_fr);
    }
  }, [brief]); // eslint-disable-line

  if (!brief) return null;
  const soir = mode === "soir";
  const Icon = soir ? Moon : Sunrise;
  return (
    <section data-testid={soir ? "evening-brief" : "morning-brief"} data-mode={mode}
      className={`rounded-3xl p-4 border ${soir ? "bg-[#0A1426] border-[#0A1426] text-white" : "bg-[#FFF6DF] border-[#D4AF5A]/40"}`}>
      <div className="flex items-center gap-3">
        <span className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${soir ? "bg-white/15 text-[#D4AF5A]" : "bg-[#D4AF5A]/25 text-[#8A6D2B]"}`}><Icon size={24} /></span>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] uppercase tracking-wider font-bold ${soir ? "text-white/70" : "text-[#8A6D2B]"}`}>
            {soir ? (lang === "ln" ? "Bilan ya mpokwa" : "Bilan du soir") : (lang === "ln" ? "Programme ya lelo" : "Résumé du jour")}
          </div>
          <div className={`font-[Fraunces] text-lg font-bold leading-tight ${soir ? "text-white" : "text-[#0A1426]"}`}>
            {soir ? (lang === "ln" ? `Mpokwa malamu ${brief.prenom}` : `Bonsoir ${brief.prenom}`) : (lang === "ln" ? `Mbote ${brief.prenom}` : `Bonjour ${brief.prenom}`)}
            {soir && brief.total ? <span data-testid="evening-score" className="ml-2 text-[13px] font-bold text-[#D4AF5A]">{brief.faits}/{brief.total}</span> : null}
          </div>
        </div>
        <button data-testid="brief-listen-btn" onClick={() => (speaking ? stopSpeech() : play(text))}
          className={`h-12 px-4 rounded-full font-bold text-[14px] flex items-center gap-2 ${speaking ? "bg-[#D9534F] text-white" : soir ? "bg-[#D4AF5A] text-[#0A1426]" : "bg-[#0A1426] text-white"}`}>
          {speaking ? <><VolumeX size={18} /> Stop</> : <><Volume2 size={18} /> {lang === "ln" ? "Yoka" : "Écouter"}</>}
        </button>
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5">
        {brief.items.map((it) => (
          <li key={it.code + (it.label || "")} data-testid={`brief-item-${it.code}`} className={`flex items-center gap-1.5 text-[13px] ${soir ? "text-white" : "text-[#0A1426]"}`}>
            {it.fait ? <Check size={16} className="text-[#5CB85C] shrink-0" /> : <Circle size={14} className="text-[#D4AF5A] shrink-0" />}
            <span className={it.fait ? "line-through opacity-60" : "font-bold"}>{it.label}</span>
          </li>
        ))}
      </ul>
      <p data-testid="brief-text" className={`text-[12px] mt-3 leading-snug line-clamp-3 ${soir ? "text-white/75" : "text-[#0A1426]/70"}`}>{text}</p>
      {voice && <div data-testid="brief-voice-mode" className={`text-[10px] mt-1 ${soir ? "text-white/50" : "text-[#0A1426]/50"}`}>{voice === "naturelle" ? "Voix naturelle (ElevenLabs)" : "Voix du navigateur"}</div>}
    </section>
  );
}
